import getConfig from 'next/config';
import {
  Grid,
  Button,
  Box,
  Typography,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
} from '@mui/material';
import { PictogramJobPost, PictogramSuccess, PictogramMatching } from '@care/react-embrace-icons';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import { useResponsive } from '@/components/hooks/useResponsive';

const {
  publicRuntimeConfig: { NEXT_PUBLIC_APP_URL },
} = getConfig();

interface HowItWorksProps {
  segmentNameSingularLowerCase?: string;
  segmentationUrl: string;
  isCostGuideLandingPage?: boolean;
}

export const HowItWorks = ({
  segmentationUrl,
  isCostGuideLandingPage = false,
  segmentNameSingularLowerCase,
}: HowItWorksProps) => {
  const { isSmallViewport } = useResponsive();
  const ctaText = isCostGuideLandingPage
    ? 'Join free to get started'
    : `View ${segmentNameSingularLowerCase} profiles`;

  const handleCtaBtn = () => {
    const amplitudeData = {
      cta_text: ctaText,
      cta_type: 'button',
      url: `${NEXT_PUBLIC_APP_URL}${segmentationUrl}`,
      url_path: segmentationUrl,
    };

    trackAmplitudeEvent(AmplitudeEvent.ctaInteracted, amplitudeData);
  };

  const hiwSteps = [
    {
      content: 'Share details about your needs',
      icon: <PictogramJobPost size={isSmallViewport ? '64px' : '128px'} />,
    },
    {
      content: 'Match with highly rated local caregivers near you',
      icon: <PictogramMatching size={isSmallViewport ? '64px' : '128px'} />,
    },
    {
      content: 'Compare options and choose the best fit',
      icon: <PictogramSuccess size={isSmallViewport ? '64px' : '128px'} />,
    },
  ];

  const hiwStepsMobile = () => {
    return (
      <Grid item container>
        {hiwSteps.map((item) => (
          <ListItem key={item.content} sx={{ mb: 3 }}>
            <ListItemIcon sx={{ mr: 3 }}>{item.icon}</ListItemIcon>
            <ListItemText>
              <Typography variant="body2">{item.content}</Typography>
            </ListItemText>
          </ListItem>
        ))}
      </Grid>
    );
  };

  const hiwStepsDesktop = () => {
    return (
      <Grid item container>
        {hiwSteps.map((item) => (
          <Grid item sm={4} key={item.content}>
            <ListItem
              key={item.content}
              sx={{
                mb: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <ListItemIcon sx={{ mr: 0, mb: 3 }}>{item.icon}</ListItemIcon>
              <ListItemText sx={{ width: { md: '70%' } }}>
                <Typography variant="body1" textAlign="center">
                  {item.content}
                </Typography>
              </ListItemText>
            </ListItem>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Container
      maxWidth="xl"
      disableGutters={!isSmallViewport}
      sx={{
        mt: { xs: 6, sm: 8 },
      }}>
      <Typography
        component="h2"
        variant={isSmallViewport ? 'h1' : 'display3'}
        mb={{ xs: 3, sm: 6 }}
        textAlign={{ xs: 'left', sm: 'center' }}>
        Find caregivers near you that you&apos;ll love
      </Typography>
      {isSmallViewport ? hiwStepsMobile() : hiwStepsDesktop()}
      <Box
        width={{ xs: '270px', sm: '315px', md: '362px' }}
        sx={{
          pb: 5,
          mx: 'auto',
        }}>
        <Button
          sx={{
            mt: { xs: 2, md: 1 },
          }}
          color="primary"
          component="a"
          fullWidth
          onClick={handleCtaBtn}
          href={segmentationUrl}
          size="large"
          variant="contained"
          rel={isCostGuideLandingPage ? 'nofollow' : ''}>
          {ctaText}
        </Button>
      </Box>
    </Container>
  );
};
